import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Clock,
  Info,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
  RequirementsPageData,
  FrameworkData,
  GroupData,
  ItemData,
  Status,
  ThresholdData,
} from './types/requirements';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { useOrganization } from '../../contexts/OrganizationContext';
import { apiEndpoints } from '../../utils/api';
import toast from '../../design-system/toast';
import { ComplianceEmptyState } from '../../components/compliance/ComplianceEmptyState';
import { ThresholdDetailsPanel } from '../../components/compliance/ThresholdDetailsPanel';
import { MultiSelectDropdown } from '../../components/common/MultiSelectDropdown';
import { SingleSelectDropdown } from '../../components/common/SingleSelectDropdown';
import {
  ThresholdDetailsPanelProvider,
  useThresholdDetailsPanel,
} from '../../contexts/ThresholdDetailsPanelContext';

// ============================================================================
// Status Badge Component
// ============================================================================

interface StatusBadgeProps {
  status: Status;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  if (status === 'passing') {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary">
        Passing
      </span>
    );
  }
  return (
    <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-500">
      Failing
    </span>
  );
};

// ============================================================================
// Thresholds Table Component
// ============================================================================

// Map scanType labels back to API values
const getScanTypeApiValue = (label: string): string => {
  const labelLower = label.toLowerCase();
  if (labelLower.includes('semgrep')) return 'semgrep_scan';
  if (labelLower.includes('secret')) return 'secrets_scan';
  if (labelLower.includes('deep ai') || labelLower.includes('deep_ai')) return 'deep_ai_scan';
  return 'semgrep_scan';
};

// Extract severity from label
const getSeverityFromLabel = (label: string): string => {
  const labelLower = label.toLowerCase();
  if (labelLower.includes('high') || labelLower.includes('error')) return 'high';
  if (labelLower.includes('medium') || labelLower.includes('warning')) return 'medium';
  if (labelLower.includes('low') || labelLower.includes('info')) return 'low';
  return 'high';
};

// Get display name for scan type (cleaner format)
const getScanTypeDisplayName = (label: string): string => {
  const labelLower = label.toLowerCase();
  if (labelLower.includes('deep ai') || labelLower.includes('deep_ai')) return 'Deep AI Scan';
  if (labelLower.includes('semgrep')) return 'Semgrep';
  if (labelLower.includes('secret')) return 'Secrets';
  if (labelLower.includes('ai')) return 'AI Scan';
  return 'Scan';
};

// Severity badge component
interface SeverityBadgeProps {
  severity: string;
}

const SeverityBadge: React.FC<SeverityBadgeProps> = ({ severity }) => {
  const severityLower = severity.toLowerCase();

  if (severityLower === 'critical') {
    return (
      <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400 font-medium uppercase">
        Critical
      </span>
    );
  }
  if (severityLower === 'high') {
    return (
      <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-medium uppercase">
        High
      </span>
    );
  }
  if (severityLower === 'medium') {
    return (
      <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-medium uppercase">
        Medium
      </span>
    );
  }
  if (severityLower === 'low') {
    return (
      <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 font-medium uppercase">
        Low
      </span>
    );
  }
  return (
    <span className="text-xs px-1.5 py-0.5 rounded bg-gray-500/20 text-gray-400 font-medium uppercase">
      {severity}
    </span>
  );
};

interface ThresholdsTableProps {
  item: ItemData;
  selectedApps?: string[];
}

const ThresholdsTable: React.FC<ThresholdsTableProps> = ({ item, selectedApps }) => {
  const { openPanel } = useThresholdDetailsPanel();

  const handleThresholdClick = (threshold: ThresholdData) => {
    openPanel({
      scanType: getScanTypeApiValue(threshold.label),
      severity: getSeverityFromLabel(threshold.label),
      label: threshold.label,
      applicationIds: selectedApps?.length ? selectedApps.join(',') : undefined,
    });
  };

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-medium text-ui-text-secondary uppercase tracking-wide">
          SLA Adherence
        </h4>
        <span className="text-xs text-ui-text-tertiary">
          {item.score.passing}/{item.score.total} passing
        </span>
      </div>
      <div className="border border-ui-border-primary rounded-lg overflow-x-auto">
        <table className="w-full min-w-[600px] text-sm">
          <thead>
            <tr className="bg-ui-background-tertiary">
              <th className="text-left text-xs font-medium text-ui-text-secondary px-3 py-2">Scan Type / Severity</th>
              <th className="text-right text-xs font-medium text-ui-text-secondary px-3 py-2">Target SLA Adherence</th>
              <th className="text-right text-xs font-medium text-ui-text-secondary px-3 py-2">Actual SLA Adherence</th>
              <th className="text-center text-xs font-medium text-ui-text-secondary px-3 py-2">Status</th>
              <th className="text-right text-xs font-medium text-ui-text-secondary px-3 py-2">Details</th>
            </tr>
          </thead>
          <tbody>
            {item.thresholds.map((threshold, idx) => (
              <tr
                key={threshold.id}
                onClick={() => handleThresholdClick(threshold)}
                className={`border-t border-ui-border-primary cursor-pointer hover:bg-ui-background-tertiary transition-colors ${
                  threshold.status === 'failing' ? 'bg-red-500/5 hover:bg-red-500/10' : ''
                } ${idx % 2 === 0 ? '' : 'bg-ui-background-secondary/50'}`}
              >
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      threshold.status === 'passing' ? 'bg-brand-primary' : 'bg-red-500'
                    }`} />
                    <span className="text-ui-text-primary">{getScanTypeDisplayName(threshold.label)}</span>
                    <SeverityBadge severity={getSeverityFromLabel(threshold.label)} />
                  </div>
                </td>
                <td className="px-3 py-2 text-right text-ui-text-secondary">{threshold.target}%</td>
                <td className={`px-3 py-2 text-right font-medium ${
                  threshold.status === 'passing' ? 'text-brand-primary' : 'text-red-500'
                }`}>
                  {threshold.actual}%
                </td>
                <td className="px-3 py-2 text-center">
                  {threshold.status === 'passing' ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary font-medium">
                      PASSING
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-500 font-medium">
                      FAILING
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <span className={`text-sm font-medium ${
                    threshold.status === 'passing' ? 'text-ui-text-secondary' : 'text-red-500'
                  }`}>
                    {threshold.details?.withinSla ?? 0}/{(threshold.details?.withinSla ?? 0) + (threshold.details?.outsideSla ?? 0)}
                  </span>
                  <span className="text-ui-text-tertiary ml-1">↗</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// Volume Section Component
// ============================================================================

interface VolumeSectionProps {
  volume: NonNullable<ItemData['evidence']>['volume'];
}

const VolumeSection: React.FC<VolumeSectionProps> = ({ volume }) => {
  if (!volume) return null;

  return (
    <div className="mt-4">
      <h4 className="text-xs font-medium text-ui-text-secondary uppercase tracking-wide mb-2">
        Volume
      </h4>
      <div className="space-y-2">
        {volume.byScanType.map(scan => (
          <div
            key={scan.scanType}
            className="flex items-center justify-between p-2 bg-ui-background-tertiary rounded-lg"
          >
            <span className="text-sm text-ui-text-primary">{scan.label}</span>
            <div className="flex items-center gap-4 text-xs">
              <span className="text-ui-text-secondary">
                <span className="font-medium text-ui-text-primary">{scan.detected}</span> detected
              </span>
              <span className="text-ui-text-secondary">
                <span className="font-medium text-brand-primary">{scan.resolved}</span> resolved
              </span>
              <span className="text-ui-text-secondary">
                <span className="font-medium text-amber-500">{scan.open}</span> open
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Operations Section Component
// ============================================================================

interface OperationsSectionProps {
  operations: NonNullable<ItemData['evidence']>['operations'];
}

const OperationsSection: React.FC<OperationsSectionProps> = ({ operations }) => {
  if (!operations) return null;

  const { mttr, platformHealth } = operations;

  return (
    <div className="mt-4 space-y-4">
      {mttr && mttr.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-ui-text-secondary uppercase tracking-wide mb-2">
            Mean Time to Resolve (MTTR)
          </h4>
          <div className="flex flex-wrap gap-2">
            {mttr.map(m => (
              <div
                key={`${m.scanType}-${m.severity}`}
                className="px-3 py-1.5 bg-ui-background-tertiary rounded-lg"
              >
                <span className="text-xs text-ui-text-secondary">{m.label}:</span>
                <span className="text-sm font-medium text-ui-text-primary ml-1">{m.days}d</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {platformHealth && (
        <div>
          <h4 className="text-xs font-medium text-ui-text-secondary uppercase tracking-wide mb-2">
            Platform Health
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-ui-background-tertiary rounded-lg">
              <p className="text-xs text-ui-text-secondary">Execution Rate</p>
              <p className="text-lg font-semibold text-ui-text-primary">{platformHealth.executionRate}%</p>
              <p className="text-xs text-ui-text-tertiary">
                {platformHealth.scheduledExecuted}/{platformHealth.scheduledExpected} scheduled
              </p>
            </div>
            <div className="p-3 bg-ui-background-tertiary rounded-lg">
              <p className="text-xs text-ui-text-secondary">Success Rate</p>
              <p className="text-lg font-semibold text-ui-text-primary">{platformHealth.successRate}%</p>
              <p className="text-xs text-ui-text-tertiary">
                {platformHealth.scansSucceeded} succeeded, {platformHealth.scansFailed} failed
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Item Row Component (Expandable Control/Criteria)
// ============================================================================

interface ItemRowProps {
  item: ItemData;
  itemLabel: string;
  isExpanded: boolean;
  onToggle: () => void;
  selectedApps?: string[];
}

const ItemRow: React.FC<ItemRowProps> = ({ item, isExpanded, onToggle, selectedApps }) => {
  return (
    <div className="border-l-2 border-ui-border-primary ml-4">
      <button
        onClick={onToggle}
        className="w-full flex items-center flex-wrap gap-2 p-3 hover:bg-ui-background-tertiary transition-colors text-left"
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-ui-text-tertiary flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-ui-text-tertiary flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs font-medium text-ui-text-tertiary flex-shrink-0">{item.id}</span>
            <span className="text-sm text-ui-text-primary truncate min-w-0">{item.title}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-xs text-ui-text-tertiary">
            {item.score.passing}/{item.score.total}
          </span>
          <StatusBadge status={item.status} />
        </div>
      </button>
      {isExpanded && (
        <div className="px-4 pb-4 ml-6 border-t border-ui-border-primary">
          <p className="text-xs text-ui-text-secondary mt-3 mb-3">{item.description}</p>
          <ThresholdsTable item={item} selectedApps={selectedApps} />
          {item.evidence && (
            <>
              <VolumeSection volume={item.evidence.volume} />
              <OperationsSection operations={item.evidence.operations} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Group Section Component (Expandable Clause/Category)
// ============================================================================

interface GroupSectionProps {
  group: GroupData;
  groupLabel: string;
  itemLabel: string;
  isExpanded: boolean;
  expandedItems: Set<string>;
  onToggleGroup: () => void;
  onToggleItem: (itemId: string) => void;
  selectedApps?: string[];
}

const GroupSection: React.FC<GroupSectionProps> = ({
  group,
  groupLabel,
  itemLabel,
  isExpanded,
  expandedItems,
  onToggleGroup,
  onToggleItem,
  selectedApps,
}) => {
  return (
    <div className="border border-ui-border-primary rounded-lg overflow-hidden">
      <button
        onClick={onToggleGroup}
        className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 p-4 text-left hover:bg-ui-background-tertiary transition-colors"
      >
        <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1 w-full sm:w-auto">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-ui-text-tertiary flex-shrink-0 mt-0.5 sm:mt-0" />
          ) : (
            <ChevronRight className="w-5 h-5 text-ui-text-tertiary flex-shrink-0 mt-0.5 sm:mt-0" />
          )}
          <div className="text-left min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-ui-text-tertiary">{groupLabel} {group.id}</span>
              <span className="text-sm font-medium text-ui-text-primary">{group.title}</span>
            </div>
            <p className="text-xs text-ui-text-secondary mt-0.5">{group.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 self-start sm:self-center pl-8 sm:pl-0">
          <span className="text-sm text-ui-text-secondary">
            {group.score.passing}/{group.score.total} {itemLabel.toLowerCase()}s
          </span>
          <StatusBadge status={group.status} />
        </div>
      </button>
      {isExpanded && (
        <div className="border-t border-ui-border-primary bg-ui-background-secondary/30">
          {group.items.map(item => (
            <ItemRow
              key={item.id}
              item={item}
              itemLabel={itemLabel}
              isExpanded={expandedItems.has(item.id)}
              onToggle={() => onToggleItem(item.id)}
              selectedApps={selectedApps}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Framework Content Component (Right Panel)
// ============================================================================

interface FrameworkContentProps {
  framework: FrameworkData;
  expandedGroups: Set<string>;
  expandedItems: Set<string>;
  onToggleGroup: (groupId: string) => void;
  onToggleItem: (itemId: string) => void;
  selectedApps?: string[];
}

const FrameworkContent: React.FC<FrameworkContentProps> = ({
  framework,
  expandedGroups,
  expandedItems,
  onToggleGroup,
  onToggleItem,
  selectedApps,
}) => {
  const [showMethodology, setShowMethodology] = useState(false);

  if (!framework.enabled) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-ui-text-secondary">
          This framework is currently disabled. Enable it in settings to view requirements.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Framework Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold text-ui-text-primary">{framework.name}</h2>
          <p className="text-sm text-ui-text-secondary mt-1">
            {framework.score.passing}/{framework.score.total} {framework.itemLabel.toLowerCase()}s passing
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-2xl font-bold ${
            framework.status === 'passing' ? 'text-brand-primary' : 'text-red-500'
          }`}>
            {framework.score.percentage}%
          </span>
          <StatusBadge status={framework.status} />
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-ui-background-tertiary rounded-full overflow-hidden mb-4">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            framework.status === 'passing' ? 'bg-brand-primary' : 'bg-red-500'
          }`}
          style={{ width: `${framework.score.percentage}%` }}
        />
      </div>

      {/* Methodology explainer */}
      <div className="mb-6">
        <button
          onClick={() => setShowMethodology(!showMethodology)}
          className="flex items-center gap-1.5 text-xs text-ui-text-tertiary hover:text-ui-text-secondary transition-colors"
        >
          <Info className="w-3.5 h-3.5" />
          <span>How is this calculated?</span>
          {showMethodology ? (
            <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronRight className="w-3 h-3" />
          )}
        </button>
        {showMethodology && (
          <div className="mt-2 p-4 bg-ui-background-secondary border border-ui-border-primary rounded-lg text-sm text-ui-text-secondary space-y-3">
            <div>
              <p className="font-medium text-ui-text-primary mb-1">Pass/Fail Status</p>
              <p>
                Each control is evaluated by its SLA adherence — the percentage of findings resolved within their
                target remediation window. A control passes when its actual adherence meets or exceeds the target
                (default 90%). A group or framework passes only when all of its controls pass.
              </p>
            </div>
            <div>
              <p className="font-medium text-ui-text-primary mb-1">SLA Adherence Calculation</p>
              <p>
                Adherence is calculated per scan type and severity as:
              </p>
              <p className="mt-1 px-3 py-1.5 bg-ui-background-tertiary rounded font-mono text-xs">
                adherence = resolved within SLA / (resolved within SLA + breached open + breached resolved)
              </p>
            </div>
            <div>
              <p className="font-medium text-ui-text-primary mb-1">What counts</p>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  <span className="font-medium text-ui-text-primary">Open findings still within SLA</span> — not included in the calculation. They only count once resolved or once they breach their deadline.
                </li>
                <li>
                  <span className="font-medium text-ui-text-primary">Resolved within SLA</span> — counts toward adherence (numerator and denominator).
                </li>
                <li>
                  <span className="font-medium text-ui-text-primary">Breached &amp; still open</span> — counts against adherence (denominator only).
                </li>
                <li>
                  <span className="font-medium text-ui-text-primary">Breached &amp; later resolved</span> — still counts against adherence. Resolving a finding after the deadline does not retroactively improve the score.
                </li>
              </ul>
            </div>
            <p className="text-xs text-ui-text-tertiary">
              If there are no evaluated findings for a scan type/severity, it is treated as 100% adherent (no breaches).
            </p>
          </div>
        )}
      </div>

      {/* Groups */}
      <div className="space-y-3">
        {framework.groups.map(group => (
          <GroupSection
            key={group.id}
            group={group}
            groupLabel={framework.groupLabel}
            itemLabel={framework.itemLabel}
            isExpanded={expandedGroups.has(group.id)}
            expandedItems={expandedItems}
            onToggleGroup={() => onToggleGroup(group.id)}
            onToggleItem={onToggleItem}
            selectedApps={selectedApps}
          />
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const ComplianceRequirementsPage: React.FC = () => {
  // API and organization context
  const api = useAuthenticatedApi();
  const { currentOrganization } = useOrganization();
  const { frameworkId } = useParams<{ frameworkId?: string }>();
  const navigate = useNavigate();

  // State
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [selectedDays, setSelectedDays] = useState(30);
  const [requirementsData, setRequirementsData] = useState<RequirementsPageData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [applications, setApplications] = useState<{ id: string; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Fetch applications on mount
  useEffect(() => {
    const fetchApplications = async () => {
      if (!currentOrganization) return;
      try {
        const response = await api.get<{ applications: { id: string; name: string }[]; total: number }>(
          apiEndpoints.applications.list(currentOrganization.id)
        );
        setApplications(response.applications.map(app => ({ id: app.id, name: app.name })));
      } catch (error) {
        console.error('Failed to fetch applications:', error);
      }
    };
    fetchApplications();
  }, [api, currentOrganization]);

  // Fetch requirements data from the API
  const fetchRequirementsData = useCallback(async () => {
    if (!currentOrganization) return;
    setIsLoading(true);
    setError(null);
    try {
      // Build application_ids parameter if filtering
      const appIdsParam = selectedApps.length > 0 ? selectedApps.join(',') : undefined;
      const response = await api.get<RequirementsPageData>(
        apiEndpoints.compliance.requirements(currentOrganization.id, selectedDays, appIdsParam)
      );
      setRequirementsData(response);
    } catch (err: any) {
      console.error('Failed to fetch requirements data:', err);
      setError('Failed to load compliance requirements. Please try again.');
      if (err?.status >= 500) {
        toast.error('Failed to load compliance requirements');
      }
    } finally {
      setIsLoading(false);
    }
  }, [api, currentOrganization, selectedDays, selectedApps]);

  // Fetch data on mount and when days or apps change
  useEffect(() => {
    fetchRequirementsData();
  }, [fetchRequirementsData]);

  // Use API data or fallback to empty state
  const data = requirementsData;

  // Get current framework from URL param
  const currentFramework = data?.frameworks.find(f => f.id === frameworkId) ?? data?.frameworks[0];

  // Redirect to first enabled framework if none specified or invalid
  useEffect(() => {
    if (data && data.frameworks.length > 0 && !frameworkId) {
      const firstEnabled = data.frameworks.find(f => f.enabled) ?? data.frameworks[0];
      navigate(`/compliance/requirements/${firstEnabled.id}`, { replace: true });
    }
  }, [data, frameworkId, navigate]);

  // Applications loaded from API (see useEffect above)

  // Toggle handlers
  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const toggleItem = (itemId: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  // Loading state with skeleton loaders
  if (isLoading && !data) {
    return (
      <div className="min-h-screen bg-ui-background-primary">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Header skeleton */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="animate-pulse">
              <div className="h-8 bg-ui-background-secondary rounded w-48 mb-2" />
              <div className="h-4 bg-ui-background-secondary rounded w-80" />
            </div>
            <div className="flex items-center gap-3">
              <div className="animate-pulse h-10 bg-ui-background-secondary rounded w-32" />
              <div className="animate-pulse h-10 bg-ui-background-secondary rounded w-40" />
            </div>
          </div>

          {/* Content skeleton */}
          <div className="animate-pulse">
            <div className="h-7 bg-ui-background-secondary rounded w-48 mb-2" />
            <div className="h-4 bg-ui-background-secondary rounded w-40 mb-6" />
            <div className="h-2 bg-ui-background-secondary rounded-full mb-6" />
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-20 bg-ui-background-secondary border border-ui-border-primary rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Empty state (no data and not loading)
  if (!data) {
    return (
      <div className="min-h-screen bg-ui-background-primary">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Error Banner */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <span className="text-red-800 dark:text-red-200">{error}</span>
                </div>
                <button
                  onClick={() => { setError(null); fetchRequirementsData(); }}
                  className="text-red-600 dark:text-red-400 underline hover:text-red-800 dark:hover:text-red-200 text-sm"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-ui-text-primary">Requirements</h1>
              <p className="text-sm text-ui-text-secondary mt-1">
                Compliance metrics are based on SLA adherence — how well findings are resolved within their target remediation windows.
              </p>
            </div>
          </div>
          <div className="py-16">
            <ComplianceEmptyState
              variant={error ? 'error' : 'no-data'}
              title={error ? 'Failed to load requirements' : 'No compliance data'}
              description={error ? 'Please try again or contact support if the issue persists.' : 'Run a compliance refresh from the Overview page to generate metrics.'}
              action={error ? {
                label: 'Retry',
                onClick: () => { setError(null); fetchRequirementsData(); },
                isLoading: isLoading,
              } : undefined}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ui-background-primary">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <span className="text-red-800 dark:text-red-200">{error}</span>
              </div>
              <button
                onClick={() => { setError(null); fetchRequirementsData(); }}
                className="text-red-600 dark:text-red-400 underline hover:text-red-800 dark:hover:text-red-200 text-sm"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-ui-text-primary">Requirements</h1>
            <p className="text-sm text-ui-text-secondary mt-1">
              Compliance metrics are based on SLA adherence — how well findings are resolved within their target remediation windows.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3 w-full md:w-auto">
            {/* Last computed timestamp */}
            {data.computedAt && (
              <div className="hidden sm:flex items-center gap-1.5 text-xs text-ui-text-tertiary">
                <Clock size={14} />
                <span>
                  Last computed {formatDistanceToNow(new Date(data.computedAt), { addSuffix: true })}
                </span>
              </div>
            )}
            <div className="w-full sm:w-auto [&>div>button]:w-full sm:[&>div>button]:w-auto">
              <SingleSelectDropdown<number>
                label="Date range"
                options={[30, 60, 90].map((d) => ({ value: d, label: `Last ${d} days` }))}
                value={selectedDays}
                onChange={setSelectedDays}
              />
            </div>
            <div className="w-full sm:w-auto [&>div>button]:w-full sm:[&>div>button]:w-auto">
              <MultiSelectDropdown
                label="Repositories"
                options={applications}
                selected={selectedApps}
                onChange={setSelectedApps}
              />
            </div>
            <div className="hidden sm:block px-3 py-2 bg-ui-background-secondary border border-ui-border-primary rounded-lg text-sm text-ui-text-secondary">
              {data.scope.applicationCount} apps, {data.scope.repositoryCount} repos
            </div>
          </div>
        </div>

        {/* Framework Content */}
        {currentFramework ? (
          <FrameworkContent
            framework={currentFramework}
            expandedGroups={expandedGroups}
            expandedItems={expandedItems}
            onToggleGroup={toggleGroup}
            onToggleItem={toggleItem}
            selectedApps={selectedApps}
          />
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-ui-text-secondary">
              Select a framework from the sidebar to view its requirements.
            </p>
          </div>
        )}
      </div>

      {/* Threshold Details Panel */}
      <ThresholdDetailsPanel />
    </div>
  );
};

// Wrapper component with context provider
const ComplianceRequirementsPageWithProvider: React.FC = () => {
  return (
    <ThresholdDetailsPanelProvider>
      <ComplianceRequirementsPage />
    </ThresholdDetailsPanelProvider>
  );
};

export default ComplianceRequirementsPageWithProvider;
